import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiGift,
  FiShoppingBag,
  FiCreditCard,
  FiSearch,
  FiFilter,
  FiChevronRight,
  FiShare2,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiDollarSign,
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import vouchersService, { Voucher, VoucherCompra, VoucherTipo } from '../services/vouchers.service';
import VoucherCard from '../components/VoucherCard';
import api from '../services/api';
import { fmtParede, fmtInstante } from '../utils/tempoCv';

type TabType = 'store' | 'wallet';

const Vouchers: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('store');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Store state
  const [storeVouchers, setStoreVouchers] = useState<Voucher[]>([]);
  const [categories, setCategories] = useState<VoucherTipo[]>([]);

  // Wallet state
  const [myVouchers, setMyVouchers] = useState<VoucherCompra[]>([]);
  const [walletFilter, setWalletFilter] = useState<string>('');

  // Modal state
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [selectedPurchase, setSelectedPurchase] = useState<VoucherCompra | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  // Wallet state
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Transfer modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'store') {
        const [vouchers, cats] = await Promise.all([
          vouchersService.getVouchersStore(),
          vouchersService.getCategorias(),
        ]);
        setStoreVouchers(vouchers);
        setCategories(cats);
      } else if (isAuthenticated) {
        const vouchers = await vouchersService.getMeusVouchers({ status: walletFilter || undefined });
        setMyVouchers(vouchers);
      }
    } catch (error) {
      console.error('Error loading vouchers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    setLoadingBalance(true);
    try {
      const response = await api.get<{ success: boolean; data?: { saldo_disponivel: number } }>('/client/wallet/balance');
      if (response.data.success && response.data.data) {
        setWalletBalance(response.data.data.saldo_disponivel);
      }
    } catch {
      setWalletBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedVoucher || !isAuthenticated) return;

    setPurchasing(true);
    setPurchaseError(null);

    try {
      const purchase = await vouchersService.comprarVoucher(selectedVoucher.id);
      setPurchaseSuccess(true);
      setSelectedPurchase(purchase);
      // Update wallet balance after purchase
      fetchWalletBalance();
      // Refresh wallet tab
      if (activeTab === 'wallet') {
        loadData();
      }
    } catch (error: any) {
      setPurchaseError(error.message || t('vouchers.errorPurchasing'));
      // Refresh balance in case it changed
      fetchWalletBalance();
    } finally {
      setPurchasing(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedPurchase || !transferEmail) return;

    setTransferring(true);
    try {
      await vouchersService.transferirVoucher(selectedPurchase.id, {
        destinatario_email: transferEmail,
      });
      setShowTransferModal(false);
      setTransferEmail('');
      loadData();
      alert(t('vouchers.voucherTransferred'));
    } catch (error: any) {
      alert(error.message || t('vouchers.errorTransferring'));
    } finally {
      setTransferring(false);
    }
  };

  const filteredStoreVouchers = storeVouchers.filter((v) => {
    const matchesSearch = !searchTerm ||
      v.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.empresa_nome.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || v.tipo_categoria === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredWalletVouchers = myVouchers.filter((v) => {
    const matchesSearch = !searchTerm ||
      v.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.empresa_nome.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-pagali-primary to-pagali-secondary text-white p-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FiGift /> {t('vouchers.title')}
        </h1>
        <p className="text-white/80 mt-1">{t('vouchers.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="flex">
          <button
            onClick={() => setActiveTab('store')}
            className={`flex-1 py-4 text-center font-medium transition-colors ${
              activeTab === 'store'
                ? 'text-pagali-primary border-b-2 border-pagali-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiShoppingBag className="inline-block mr-2" />
            {t('vouchers.store')}
          </button>
          <button
            onClick={() => setActiveTab('wallet')}
            className={`flex-1 py-4 text-center font-medium transition-colors ${
              activeTab === 'wallet'
                ? 'text-pagali-primary border-b-2 border-pagali-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiCreditCard className="inline-block mr-2" />
            {t('vouchers.myWallet')}
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="p-4 bg-white border-b">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t('vouchers.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary"
          />
        </div>

        {activeTab === 'store' && categories.length > 0 && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                !selectedCategory
                  ? 'bg-pagali-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t('common.all')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.tipo)}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  selectedCategory === cat.tipo
                    ? 'bg-pagali-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.nome}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'wallet' && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
            {['', 'ativo', 'usado', 'expirado'].map((status) => (
              <button
                key={status}
                onClick={() => {
                  setWalletFilter(status);
                  loadData();
                }}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  walletFilter === status
                    ? 'bg-pagali-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status === '' ? t('common.all') : vouchersService.getStatusLabel(status)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Promo Banner */}
      {activeTab === 'store' && (
        <div className="mx-4 mt-4 p-5 rounded-2xl bg-gradient-to-r from-pink-50 via-fuchsia-50 to-purple-50 border border-pink-200/40 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center flex-shrink-0 shadow-md">
              <FiGift size={26} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="inline-block px-2 py-0.5 text-[10px] font-bold tracking-widest text-purple-600 bg-purple-100 rounded mb-1">
                LANÇAMENTO
              </span>
              <h3 className="text-base font-bold text-gray-900 leading-tight">
                Pagali + Encosta Azul GuestHouse
              </h3>
              <p className="text-sm text-gray-500 leading-snug mt-0.5">
                Desconto exclusivo na GuestHouse Encosta Azul em Cidade Velha. Adere à Pagali e aproveita ofertas de parceiros!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-pagali-primary border-t-transparent" />
          </div>
        ) : activeTab === 'store' ? (
          // Store — bank-card style
          <div className="space-y-5 max-w-xl mx-auto">
            {filteredStoreVouchers.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 mx-auto rounded-3xl bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center mb-5">
                  <FiGift size={40} className="text-purple-300" />
                </div>
                <p className="text-lg font-semibold text-gray-700">Sem vouchers de momento</p>
                <p className="text-sm text-gray-400 mt-1">Os vouchers das empresas parceiras vão aparecer aqui.</p>
              </div>
            ) : (
              filteredStoreVouchers.map((voucher) => {
                const isDesconto = voucher.tipo_categoria === 'desconto';
                const bgClass = isDesconto
                  ? 'from-blue-50 via-indigo-50 to-purple-50'
                  : 'from-violet-50 via-purple-50 to-fuchsia-50';
                const accentClass = isDesconto ? 'text-blue-700' : 'text-purple-700';
                const accentBg = isDesconto ? 'bg-blue-700/10' : 'bg-purple-700/10';

                return (
                  <div
                    key={voucher.id}
                    onClick={() => {
                      setSelectedVoucher(voucher);
                      if (isAuthenticated) fetchWalletBalance();
                    }}
                    className={`cursor-pointer rounded-2xl bg-gradient-to-r ${bgClass} border border-purple-100/40 shadow-sm hover:shadow-lg transition-all p-0 overflow-hidden`}
                  >
                    {/* Top section */}
                    <div className="px-5 pt-5 pb-3">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-xl ${accentBg} flex items-center justify-center flex-shrink-0`}>
                          <FiGift size={18} className={accentClass} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 truncate">{voucher.empresa_nome}</p>
                          <p className="font-bold text-gray-900 truncate leading-tight">{voucher.titulo}</p>
                        </div>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${accentBg} ${accentClass}`}>
                          {voucher.tipo_categoria === 'valor' ? 'Gift Card' : 'Desconto'}
                        </span>
                      </div>
                      <p className={`text-3xl font-extrabold mt-3 tracking-tight ${accentClass}`}>
                        {isDesconto && voucher.percentagem_desconto
                          ? `${voucher.percentagem_desconto}% OFF`
                          : voucher.valor
                          ? `${voucher.valor.toLocaleString('pt-CV')} ECV`
                          : '-'}
                      </p>
                    </div>

                    {/* Dashed divider */}
                    <div className="relative h-5 flex items-center">
                      <div className="flex-1 mx-6 border-t border-dashed border-purple-200/60" />
                      <div className="absolute -left-2.5 w-5 h-5 rounded-full bg-white" />
                      <div className="absolute -right-2.5 w-5 h-5 rounded-full bg-white" />
                    </div>

                    {/* Bottom section */}
                    <div className="px-5 pb-5 pt-1 flex justify-between items-end">
                      <div>
                        <p className="text-xs text-gray-400">Preço</p>
                        <p className="text-lg font-bold text-gray-900">
                          {voucher.preco_venda > 0
                            ? `${voucher.preco_venda.toLocaleString('pt-CV')} ECV`
                            : 'Gratuito'}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          voucher.disponivel > 0
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-red-50 text-red-600 border border-red-200'
                        }`}>
                          {voucher.disponivel > 0
                            ? `${voucher.disponivel} disponível`
                            : 'Esgotado'}
                        </span>
                        {voucher.data_validade && (
                          <p className="text-xs text-gray-400 mt-1">
                            até {fmtParede(voucher.data_validade, { day: '2-digit', month: '2-digit', year: 'numeric' }, 'pt-PT')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : !isAuthenticated ? (
          <div className="text-center py-12">
            <FiAlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 mb-4">{t('auth.signInToSeeVouchers')}</p>
            <a
              href="/login"
              className="inline-block px-6 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/90"
            >
              {t('auth.loginTitle')}
            </a>
          </div>
        ) : (
          // Wallet List
          <div className="space-y-4">
            {filteredWalletVouchers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FiCreditCard size={48} className="mx-auto mb-4 opacity-50" />
                <p>{t('vouchers.noVouchersInWallet')}</p>
                <button
                  onClick={() => setActiveTab('store')}
                  className="mt-4 text-pagali-primary hover:underline"
                >
                  {t('vouchers.viewStore')}
                </button>
              </div>
            ) : (
              filteredWalletVouchers.map((voucher) => (
                <div
                  key={voucher.id}
                  onClick={() => setSelectedPurchase(voucher)}
                  className="cursor-pointer"
                >
                  <VoucherCard
                    titulo={voucher.titulo}
                    valor={voucher.valor}
                    percentagem_desconto={voucher.percentagem_desconto}
                    tipo_categoria={voucher.tipo_categoria}
                    cor_fundo={voucher.cor_fundo}
                    cor_texto={voucher.cor_texto}
                    empresa_nome={voucher.empresa_nome}
                    empresa_logo={voucher.empresa_logo}
                    beneficiario_nome={voucher.beneficiario_nome}
                    data_validade={voucher.data_validade}
                    qr_code_data={voucher.qr_code_data}
                    codigo_unico={voucher.codigo_unico}
                    status={voucher.status}
                  />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Store Voucher Detail Modal */}
      {selectedVoucher && !purchaseSuccess && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center">
          <div className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="font-bold text-lg">{t('vouchers.voucherDetails')}</h2>
              <button
                onClick={() => {
                  setSelectedVoucher(null);
                  setPurchaseError(null);
                  setWalletBalance(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="p-4">
              <VoucherCard
                titulo={selectedVoucher.titulo}
                valor={selectedVoucher.valor}
                percentagem_desconto={selectedVoucher.percentagem_desconto}
                tipo_categoria={selectedVoucher.tipo_categoria}
                cor_fundo={selectedVoucher.cor_fundo}
                cor_texto={selectedVoucher.cor_texto}
                empresa_nome={selectedVoucher.empresa_nome}
                empresa_logo={selectedVoucher.empresa_logo}
                data_validade={selectedVoucher.data_validade}
              />

              <div className="mt-4 space-y-3">
                <p className="text-gray-600">{selectedVoucher.descricao}</p>

                <div className="flex justify-between py-2 border-t">
                  <span className="text-gray-500">{t('common.price')}</span>
                  <span className="font-bold text-lg">
                    {selectedVoucher.preco_venda > 0
                      ? `${selectedVoucher.preco_venda.toLocaleString('pt-CV')} ECV`
                      : 'Gratuito'}
                  </span>
                </div>

                <div className="flex justify-between py-2 border-t">
                  <span className="text-gray-500">{t('common.available')}</span>
                  <span>{selectedVoucher.disponivel}</span>
                </div>

                {selectedVoucher.data_validade && (
                  <div className="flex justify-between py-2 border-t">
                    <span className="text-gray-500">{t('common.validUntil')}</span>
                    <span>{fmtParede(selectedVoucher.data_validade, { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                  </div>
                )}

                {/* Wallet balance info */}
                {isAuthenticated && walletBalance !== null && (
                  <div className={`flex justify-between py-2 border-t ${
                    walletBalance < selectedVoucher.preco_venda ? 'text-red-600' : 'text-green-600'
                  }`}>
                    <span className="flex items-center gap-1">
                      <FiDollarSign size={14} />
                      {t('vouchers.walletBalance')}
                    </span>
                    <span className="font-bold">
                      {walletBalance.toLocaleString('pt-CV')} ECV
                    </span>
                  </div>
                )}
              </div>

              {purchaseError && (
                <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {purchaseError}
                </div>
              )}

              {isAuthenticated ? (
                walletBalance !== null && walletBalance < selectedVoucher.preco_venda ? (
                  <div className="mt-4 space-y-3">
                    <div className="p-3 bg-yellow-50 text-yellow-700 rounded-lg text-sm">
                      {t('vouchers.insufficientBalance', { amount: (selectedVoucher.preco_venda - walletBalance).toLocaleString('pt-CV') })}
                    </div>
                    <a
                      href="/wallet"
                      className="block w-full py-3 bg-pagali-primary text-white rounded-xl font-medium text-center hover:bg-pagali-primary/90"
                    >
                      {t('vouchers.topupWallet')}
                    </a>
                  </div>
                ) : (
                  <button
                    onClick={handlePurchase}
                    disabled={purchasing || loadingBalance}
                    className="w-full mt-4 py-3 bg-pagali-primary text-white rounded-xl font-medium hover:bg-pagali-primary/90 disabled:opacity-50"
                  >
                    {purchasing
                      ? t('common.processing')
                      : loadingBalance
                      ? t('vouchers.verifyingBalance')
                      : selectedVoucher.preco_venda > 0
                      ? t('vouchers.payWithWallet', { amount: selectedVoucher.preco_venda.toLocaleString('pt-CV') })
                      : 'Obter grátis'}
                  </button>
                )
              ) : (
                <a
                  href="/login"
                  className="block w-full mt-4 py-3 bg-pagali-primary text-white rounded-xl font-medium text-center hover:bg-pagali-primary/90"
                >
                  {t('auth.signInToBuy')}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Purchase Success Modal */}
      {purchaseSuccess && selectedPurchase && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden">
            <div className="bg-green-500 text-white p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCheck size={32} />
              </div>
              <h2 className="text-xl font-bold">{t('vouchers.purchaseCompleted')}</h2>
              <p className="text-white/80 mt-1">{t('vouchers.voucherReady')}</p>
              {selectedPurchase.pagamento && (
                <p className="text-white/70 text-sm mt-2">
                  {t('vouchers.newBalance')}: {selectedPurchase.pagamento.saldo_posterior.toLocaleString('pt-CV')} ECV
                </p>
              )}
            </div>

            <div className="p-4">
              <VoucherCard
                titulo={selectedPurchase.titulo}
                valor={selectedPurchase.valor}
                percentagem_desconto={selectedPurchase.percentagem_desconto}
                tipo_categoria={selectedPurchase.tipo_categoria}
                cor_fundo={selectedPurchase.cor_fundo}
                cor_texto={selectedPurchase.cor_texto}
                empresa_nome={selectedPurchase.empresa_nome}
                empresa_logo={selectedPurchase.empresa_logo}
                beneficiario_nome={selectedPurchase.beneficiario_nome}
                data_validade={selectedPurchase.data_validade}
                qr_code_data={selectedPurchase.qr_code_data}
                codigo_unico={selectedPurchase.codigo_unico}
              />

              <div className="mt-4 flex gap-3">
                <button
                  onClick={() => {
                    setSelectedVoucher(null);
                    setSelectedPurchase(null);
                    setPurchaseSuccess(false);
                    setActiveTab('wallet');
                    loadData();
                  }}
                  className="flex-1 py-3 bg-pagali-primary text-white rounded-xl font-medium"
                >
                  {t('vouchers.viewInWallet')}
                </button>
                <button
                  onClick={() => {
                    setSelectedVoucher(null);
                    setSelectedPurchase(null);
                    setPurchaseSuccess(false);
                  }}
                  className="flex-1 py-3 border border-gray-200 rounded-xl font-medium"
                >
                  {t('common.continue')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Voucher Detail Modal */}
      {selectedPurchase && !purchaseSuccess && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center">
          <div className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="font-bold text-lg">{t('vouchers.myVoucher')}</h2>
              <button
                onClick={() => setSelectedPurchase(null)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="p-4">
              <VoucherCard
                titulo={selectedPurchase.titulo}
                valor={selectedPurchase.valor_restante || selectedPurchase.valor}
                percentagem_desconto={selectedPurchase.percentagem_desconto}
                tipo_categoria={selectedPurchase.tipo_categoria}
                cor_fundo={selectedPurchase.cor_fundo}
                cor_texto={selectedPurchase.cor_texto}
                empresa_nome={selectedPurchase.empresa_nome}
                empresa_logo={selectedPurchase.empresa_logo}
                beneficiario_nome={selectedPurchase.beneficiario_nome}
                data_validade={selectedPurchase.data_validade}
                qr_code_data={selectedPurchase.qr_code_data}
                codigo_unico={selectedPurchase.codigo_unico}
                status={selectedPurchase.status}
              />

              <div className="mt-4 space-y-3">
                <div className="flex justify-between py-2 border-t">
                  <span className="text-gray-500">{t('common.status')}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    selectedPurchase.status === 'ativo' ? 'bg-green-100 text-green-700' :
                    selectedPurchase.status === 'usado' ? 'bg-gray-100 text-gray-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {vouchersService.getStatusLabel(selectedPurchase.status)}
                  </span>
                </div>

                {selectedPurchase.tipo_categoria === 'valor' && selectedPurchase.valor_restante !== null && (
                  <div className="flex justify-between py-2 border-t">
                    <span className="text-gray-500">{t('vouchers.remainingBalance')}</span>
                    <span className="font-bold text-green-600">
                      {selectedPurchase.valor_restante.toLocaleString('pt-CV')} ECV
                    </span>
                  </div>
                )}

                <div className="flex justify-between py-2 border-t">
                  <span className="text-gray-500">{t('common.purchasedOn')}</span>
                  <span>{new Date(selectedPurchase.data_compra).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}</span>
                </div>

                {selectedPurchase.empresa_endereco && (
                  <div className="flex justify-between py-2 border-t">
                    <span className="text-gray-500">{t('common.whereToUse')}</span>
                    <span className="text-right text-sm">{selectedPurchase.empresa_endereco}</span>
                  </div>
                )}
              </div>

              {selectedPurchase.status === 'ativo' && (
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={() => setShowTransferModal(true)}
                    className="flex-1 py-3 border border-gray-200 rounded-xl font-medium flex items-center justify-center gap-2"
                  >
                    <FiShare2 size={18} />
                    {t('common.transfer')}
                  </button>
                </div>
              )}

              {/* Redemption history */}
              {selectedPurchase.resgates && selectedPurchase.resgates.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium mb-2">{t('common.usageHistory')}</h3>
                  <div className="space-y-2">
                    {selectedPurchase.resgates.map((resgate) => (
                      <div key={resgate.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                        <div className="flex justify-between">
                          <span>{resgate.empresa_nome}</span>
                          <span className="font-medium">
                            {resgate.valor_resgatado
                              ? `${resgate.valor_resgatado.toLocaleString('pt-CV')} ECV`
                              : `${resgate.desconto_aplicado}%`}
                          </span>
                        </div>
                        <p className="text-gray-500 text-xs mt-1">
                          {fmtInstante(resgate.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && selectedPurchase && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl p-6">
            <h2 className="text-lg font-bold mb-4">{t('vouchers.transferVoucher')}</h2>

            <p className="text-gray-600 text-sm mb-4">
              {t('vouchers.transferVoucherDesc')}
            </p>

            <input
              type="email"
              placeholder={t('vouchers.recipientEmail')}
              value={transferEmail}
              onChange={(e) => setTransferEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary"
            />

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setTransferEmail('');
                }}
                className="flex-1 py-3 border border-gray-200 rounded-xl font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleTransfer}
                disabled={!transferEmail || transferring}
                className="flex-1 py-3 bg-pagali-primary text-white rounded-xl font-medium disabled:opacity-50"
              >
                {transferring ? t('common.transferring') : t('common.transfer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vouchers;
